import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Save, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Spinner, Tag } from "@/components/common";
import { ListEditor } from "@/components/profile/Editors";
import LandingView from "@/components/landing/LandingView";

const TEXT = [["headline", "Headline"], ["subheadline", "Subheadline"], ["hero_cta", "Button text"], ["offer_title", "Assessment title"],
  ["offer_text", "Assessment text"], ["final_cta", "Form title"], ["whatsapp_message", "WhatsApp prefilled message"],
  ["meta_title", "SEO title"], ["meta_description", "SEO description"]];

const Pairs = ({ label, items = [], a, b, onChange, testid }) => (
  <div data-testid={testid}>
    <div className="cc-eyebrow mb-2">{label}</div>
    {items.map((it, i) => (
      <div key={i} className="mb-2 space-y-1 rounded-md border border-[#1e293b] p-2">
        <input className="cc-input" value={it[a] || ""} onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, [a]: e.target.value } : x)))} />
        <textarea rows={2} className="cc-input" value={it[b] || ""} onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, [b]: e.target.value } : x)))} />
      </div>
    ))}
  </div>
);

export default function LandingEditor() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get(`/landing-pages/${id}`).then((r) => { setData(r.data); setDraft(structuredClone(r.data.page.content || {})); }); }, [id]);
  if (!data || !draft) return <Spinner />;
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const { data: d } = await api.put(`/landing-pages/${id}`, { content: draft, ...extra });
      setData(d);
      toast.success(extra.owner_approved ? "Saved and approved" : "Saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const preview = { ...data, page: { ...data.page, content: draft } };
  return (
    <div data-testid="landing-editor-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/landing-pages" className="cc-btn cc-btn-ghost px-3"><ArrowLeft className="h-4 w-4" /></Link>
          <div><div className="font-display text-2xl font-bold uppercase text-slate-100">{data.page.title}</div><div className="font-mono text-xs text-slate-500">/lp/{data.page.slug}</div></div>
          {data.page.owner_approved ? <Tag tone="emerald">Approved by owner</Tag> : <Tag tone="amber">Awaiting owner review</Tag>}
          <Tag tone={data.page.published ? "emerald" : "muted"}>{data.page.published ? "Published" : "Draft"}</Tag>
        </div>
        <div className="flex gap-3">
          <button data-testid="lp-editor-save" onClick={() => save()} disabled={saving} className="cc-btn cc-btn-ghost"><Save className="h-4 w-4" /> Save</button>
          <button data-testid="lp-editor-save-approve" onClick={() => save({ owner_approved: true })} disabled={saving} className="cc-btn cc-btn-primary"><BadgeCheck className="h-4 w-4" /> Save & approve</button>
        </div>
      </div>
      <div className="grid gap-6 xl:grid-cols-[420px_1fr]">
        <div className="cc-card max-h-[80vh] space-y-4 overflow-y-auto p-5" data-testid="lp-editor-form">
          {TEXT.map(([k, label]) => (
            <label key={k} className="block"><span className="cc-eyebrow">{label}</span>
              <textarea data-testid={`lp-edit-${k}`} rows={k.includes("text") || k.includes("sub") || k === "guarantee" ? 3 : 1} className="cc-input mt-1.5 resize-y" value={draft[k] || ""} onChange={(e) => set(k, e.target.value)} /></label>
          ))}
          <ListEditor label="Why us" testid="lp-edit-why-us" items={draft.why_us || []} onChange={(v) => set("why_us", v)} />
          <ListEditor label="Form service options" testid="lp-edit-form-services" items={draft.form_services || []} onChange={(v) => set("form_services", v)} />
          <Pairs label="Assessment cards (2×2)" testid="lp-edit-assessment-cards" items={draft.assessment_cards} a="title" b="text" onChange={(v) => set("assessment_cards", v)} />
          <Pairs label="Benefits" testid="lp-edit-benefits" items={draft.benefits} a="title" b="text" onChange={(v) => set("benefits", v)} />
          <Pairs label="FAQs" testid="lp-edit-faq" items={draft.faq} a="q" b="a" onChange={(v) => set("faq", v)} />
        </div>
        <div className="overflow-hidden rounded-md border border-[#1e293b]" data-testid="lp-editor-preview">
          <div className="max-h-[80vh] overflow-y-auto"><LandingView data={preview} preview /></div>
        </div>
      </div>
    </div>
  );
}
